import { NgClass } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';

import { CookieService } from 'ngx-cookie';
import { Subscription } from 'rxjs';

import { AuthRoutes } from '@bn/auth/enums/auth-routes.enum';
import { ContentSlide } from '@bn/auth/models/sliders/content-slide.model';
import { AuthService } from '@bn/auth/services/auth.service';
import { AuthSliderService } from '@bn/auth/services/auth-slider.service';
import { APP_CONFIG, StringStringMap } from '@bn/primitives';
import { LazyLoadDirective, MarkAllTouchedDirective } from '@bn/web-ui';
import { AlertComponent } from '@bn/web-ui/alert/alert.component';
import { InputAction } from '@bn/web-ui/input/enums';
import { InputOptions } from '@bn/web-ui/input/models';
import { TwoFactorCodeComponent } from '@bn/web-ui/two-factor-code/two-factor-code.component';

import { ContentSliderComponent } from '../../components/content-slider/content-slider.component';

@Component({
  selector: 'bn-auth-two-factor-auth',
  templateUrl: './two-factor-auth.page.html',
  styleUrls: ['./two-factor-auth.page.scss'],
  host: { class: 'bn-auth__page-container' },
  imports: [
    ReactiveFormsModule,
    RouterLink,
    NgClass,
    AlertComponent,
    ContentSliderComponent,
    TwoFactorCodeComponent,
    MarkAllTouchedDirective,
    LazyLoadDirective,
  ],
})
export class TwoFactorAuthPage implements OnDestroy {
  private readonly route = inject(ActivatedRoute);

  protected readonly appConfig = inject(APP_CONFIG);
  private readonly authService = inject(AuthService);
  private readonly cookie = inject(CookieService);
  private readonly authSliderService = inject(AuthSliderService);

  public readonly authRoutes = AuthRoutes;
  public isLoading = {
    initial: true,
    submit: false,
  };
  public isError = false;
  public form = new UntypedFormGroup({
    twoFactorCode: new UntypedFormControl('', [Validators.required]),
  });

  public sliderConfig?: ContentSlide[];
  public readonly passwordInputSettings: Partial<InputOptions> = {
    rightAddons: [
      {
        icon: 'eye',
        action: InputAction.ShowPassword,
      },
    ],
  };

  private subscriptions = new Subscription();

  constructor() {
    this.getSlides();

    this.subscriptions.add(
      this.form.valueChanges.subscribe(() => {
        if (this.form.valid) this.onSubmit();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public onSubmit(): void {
    if (!this.form.valid) return;

    this.isLoading.submit = true;
    this.isError = false;

    const Code = this.form.get('twoFactorCode')?.value.code.join('');

    this.authService.verifyTwoFactorAuth(Code).subscribe({
      next: ({ auth_token }) => {
        if (!auth_token) {
          this.setError();

          return;
        }

        if (this.appConfig.extAuthCookieName && this.cookie.hasKey(this.appConfig.extAuthCookieName)) {
          const cookieWPString = this.cookie.get(this.appConfig.extAuthCookieName);
          const cookieWP: StringStringMap = cookieWPString ? JSON.parse(cookieWPString) : {};

          if (cookieWP.callbackUrl) {
            this.authService.getApiKeyValue().subscribe((apiKey) => {
              if (apiKey) this.authService.removeCookieAndRedirect(this.authService.getCallbackUrl(apiKey, cookieWP.callbackUrl));
            });

            return;
          }
        }

        // get return url from query parameters or default to home page
        const returnUrl = this.route.snapshot.queryParams['returnUrl'] ?? '/';

        this.authService.navigateAfterLogin(returnUrl).subscribe();
      },
      error: () => this.setError(),
    });
  }

  private setError(): void {
    this.isError = true;
    this.isLoading.submit = false;

    this.form.reset();
  }

  private getSlides(): void {
    this.authSliderService.get2FASlides().subscribe({
      next: (sliderConfig: ContentSlide[]) => {
        this.sliderConfig = sliderConfig;
        this.isLoading.initial = false;
      },
      error: () => (this.isLoading.initial = false),
    });
  }
}
